import React, { useRef, useImperativeHandle } from "react";
import { useReactToPrint } from "react-to-print";
import { useSelector } from "react-redux";
import moment from "moment";
import { HideDealItems, HideItemComments } from "../../utils/PosSettingConstants";
import { useCheckPosSetting } from "../../customHooks/PosCustomHooks";

const getPrintTime = () => {
    const now = new Date();
    const options = {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
    };
    return now.toLocaleString("en-US", options);
};

const CheckoutBillTemplate = React.forwardRef(({ order, items, paymentDetails, paymentDetailsInfo, paymentDetailsPartyAccountInfo, heading = "ORDER CHECKOUT BILL", hide_ItemComments = false, hide_DealItems = false }, ref) => {

    const { CompanyName } = useSelector((state) => state.authReducer);

    const gstPercentage = order?.GSTPercentage || order?.gstPercentage || 0;
    const gstPercents = gstPercentage > 0 ? `(${gstPercentage.toFixed(0)}%)` : "";


    let gstAmount = 0;
    let gstAmountCash = 0;
    let gstAmountCard = 0;
    let netBill = 0;
    let netBillCash = 0;
    let netBillCard = 0;

    if (order?.PaymentMode == 'Cash & Card') {

        gstAmountCash = (order?.totalAmountWithoutGST * (order?.cashSection?.GSTPercentage / 100)).toFixed();
        netBillCash = order?.totalAmountWithoutGST + parseFloat(gstAmountCash) + order?.deliveryCharges - order?.discountAmount;

        gstAmountCard = (order?.totalAmountWithoutGST * (order?.cardSection?.GSTPercentage / 100)).toFixed();
        netBillCard = order?.totalAmountWithoutGST + parseFloat(gstAmountCard) + order?.deliveryCharges - order?.discountAmount;
    }
    else {
        gstAmount = (order?.totalAmountWithoutGST * (order?.GSTPercentage / 100)).toFixed();
        netBill = (order?.totalAmountWithoutGST + parseFloat(gstAmount) + order?.deliveryCharges - order?.discountAmount).toFixed();
    }

    // Organize items into parent-child structure
    const organizeOrderItems = (items) => {
        const parentItems = items;
        const result = [];

        for (const parent of parentItems) {

            const children = parent.dealSelections || [];

            result.push({
                parent,
                children,
            });
        }
        return result;
    };

    const organizedItems = organizeOrderItems(items || []);

    // Generate item rows with parent-child structure
    const itemBody = organizedItems?.map((group, groupIdx) => {
        const rows = [];

        // Parent item row
        rows.push(
            <div
                key={`parent-${groupIdx}`}
                className="flex justify-between text-sm text-black px-1 py-0.5"
            >
                <span className="w-[20%] font-semibold">{group.parent.quantity}</span>
                <span className="w-[70%] font-medium text-left">
                    {group.parent.name || group.parent.ProductName}
                    {group.parent.SizeName ? ` (${group.parent.SizeName})` : ""}
                    {group.parent.FlavourName ? ` ${group.parent.FlavourName}` : ""}
                    {group.parent.DiscountPercent > 0 && (
                        <span className="text-[10px] ml-1">
                            ({group.parent.DiscountPercent}% OFF)
                        </span>
                    )}
                </span>
                <span className="w-[30%] text-right font-semibold">
                    Rs {group.parent.PriceWithGST || group.parent.price}
                </span>
            </div>
        );

        // Child item rows
        if ((Object.keys(group?.children)?.length > 0) && hide_DealItems == false) {
            Object.values(group?.children).forEach((child, childIdx) => {
                child.forEach(child => {
                    rows.push(
                        <div
                            key={`child-${groupIdx}-${childIdx}`}
                            className="flex justify-between text-sm text-black px-1 py-0.5"
                        >
                            <span className="w-[1%]"></span>
                            <span className="w-[80%] pl-4 font-normal text-left">
                                + {child.Quantity || 1} {child.name}
                                {child.SizeName ? ` (${child.SizeName})` : ""}
                                {child.FlavourName ? ` ${child.FlavourName}` : ""}

                            </span>

                        </div>
                    );
                });
            });
        }


        if ((group.parent.SpecialInstructions && group.parent.SpecialInstructions != "") && hide_ItemComments == false) {
            rows.push(
                <div
                    key={`parent-inst-${groupIdx}`}
                    className="flex justify-between text-sm text-black px-1 py-0.5"
                >
                    <span className="w-[10%] font-semibold"></span>
                    <strong className="w-[85%] font-medium italic text-left">
                        Item Comment : {group.parent.SpecialInstructions}
                    </strong>
                </div>
            );

        }

        return rows;
    }).flat();

    return (
        <div
            ref={ref}
            style={{
                fontFamily: "Calibri",
                fontSize: "12px",
                margin: "30px",
                display: "none"
            }}
            className="orderCheckoutBill print-only"
        >
            <style>
                {`
          @media print {
            .print-only {
              display: block !important;
            }
            .no-print {
              display: none !important;
            }
          }
          @media screen {
            .print-only {
              display: none !important;
            }
          }
        `}
            </style>

            <table style={{ width: "100%" }}>
                <thead>
                    <tr>
                        <th style={{
                            textAlign: "center",
                            marginBottom: 0,
                            fontSize: "24px",
                            fontFamily: "Calibri",
                            fontWeight: 600,
                            color: "#000",
                            height: "24px"
                        }}>
                            {CompanyName}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <th style={{
                            textAlign: "center",
                            fontSize: "11px",
                            fontWeight: 400,
                            border: 0,
                            color: "#000"
                        }}>
                            {order?.branchName || "Branch Address"}
                        </th>

                    </tr>
                </tbody>
            </table>

            <div className="my-1 border-t-2 border-b-2 border-black text-center w-full">
                {/* Heading */}
                <div className="w-full text-center font-semibold text-black text-sm border-b-2 border-black bg-green-50">
                    {heading}
                </div>

                <div className="flex justify-between text-sm text-black font-medium px-1">
                    <strong>Order #</strong>
                    <span>{order?.orderNumber || order?.orderNubmer || "N/A"}</span>
                </div>
                {(order?.OrderDateTime || order?.orderDateTime) && (
                    <div className="flex justify-between text-sm text-black font-medium px-1">
                        <strong>Date/Time</strong>
                        <span>{order?.orderDateTime} </span >
                    </div>
                )}
                {/* Order Mode Row */}
                <div className="flex justify-between text-sm text-black font-medium px-1">
                    <strong>Order Mode</strong>
                    <span>{order?.orderModeName} / {order?.orderSourceName}</span >
                </div>

                {/* Table */}
                {(order?.TableName || order?.tableName) && (
                    <div className="flex justify-between text-sm text-black font-medium px-1">
                        <strong>Table</strong>
                        <span>{order.TableName || order.tableName}</span>
                    </div>
                )}

                {/* Waiter */}
                {(order?.WaiterName || order?.waiterName) && (
                    <div className="flex justify-between text-sm text-black font-medium px-1">
                        <strong>Waiter</strong>
                        <span>{order.WaiterName || order.waiterName}</span>
                    </div>
                )}

                {/* Rider */}
                {(order?.RiderName || order?.riderName) && (
                    <div className="flex justify-between text-sm text-black font-medium px-1">
                        <strong>Rider</strong>
                        <span>{order.RiderName || order.riderName}</span>
                    </div>
                )}

                {/* Customer Details */}
                {(order?.customerName || order?.CustomerName) && (
                    <div className="flex justify-between text-sm text-black font-medium px-1">
                        <strong>Customer</strong>
                        <span>{order?.customerName || order?.CustomerName}</span>
                    </div>
                )}
                {(order?.orderModeId == 111) && (order?.address || order?.Address) && (
                    <div className="flex justify-between text-sm text-black font-medium px-1">
                        <strong>Address</strong>
                        <span>{order?.address || order?.Address}</span>
                    </div>
                )}
                {(order?.phoneNumber || order?.PhoneNumber) && (
                    <div className="flex justify-between text-sm text-black font-medium px-1">
                        <strong>Phone Number</strong>
                        <span>{order?.phoneNumber || order?.PhoneNumber}</span>
                    </div>
                )}
                {(order?.AlternateNumber || order?.alternateNumber) && (
                    <div className="flex justify-between text-sm text-black font-medium px-1">
                        <strong>Alternate Number</strong>
                        <span>{order?.alternateNumber || order?.AlternateNumber}</span>
                    </div>
                )}
                {(order?.LandMark || order?.landMark) && (
                    <div className="flex justify-between text-sm text-black font-medium px-1">
                        <strong>Land Mark</strong>
                        <span>{order?.landMark || order?.LandMark}</span>
                    </div>
                )}
                {(order?.Remarks1 || order?.remarks1) && (
                    <div className="flex justify-between text-sm text-black font-medium px-1">
                        <strong>Special Instruction</strong>
                        <span>{order?.Remarks1 || order?.remarks1}</span>
                    </div>
                )}
            </div>

            <div className="w-full text-sm text-black px-1 ">
                <div className="flex justify-between font-medium text-black text-sm">
                    <strong className="w-[20%]">Qty</strong>
                    <strong className="w-[60%] text-left">Item</strong>
                    <strong className="w-[20%] text-right">Price</strong>
                </div>
            </div>

            <div className="w-full border-y-2 border-black my-1 px-1 py-1">
                <div>{itemBody}</div>
            </div>

            <div className="w-full flex flex-col text-black text-sm">
                {/* Row: Subtotal */}
                {((order?.TotalAmountWithoutGST || order?.totalAmountWithoutGST) > 0) && (
                    <div className="flex justify-between items-center py-0">
                        <span className="font-normal">Subtotal</span>
                        <span className="font-normal">Rs {order?.TotalAmountWithoutGST || order?.totalAmountWithoutGST}</span>
                    </div>
                )}

                {/* Row: Delivery Charges */}
                {((order?.DeliveryCharges || order?.deliveryCharges) > 0) && (
                    <div className="flex justify-between items-center py-0">
                        <span className="font-normal">Delivery Charges</span>
                        <span className="font-normal">Rs {order?.DeliveryCharges || order?.deliveryCharges}</span>
                    </div>
                )}

                {/* Row: Discount */}
                {((order?.DiscountAmount || order?.discountAmount || order?.Discount) > 0) && (
                    <div className="flex justify-between items-center py-0">
                        <span className="font-normal">Discount</span>
                        <span className="font-normal">Rs {order?.DiscountAmount || order?.discountAmount || order?.Discount}</span>
                    </div>
                )}

                {/* Single Bill Section */}
                {/* Row: GST */}
                {((order?.TotalGST || order?.GSTAmount || order?.totalGST || order?.gstAmount) > 0) && (
                    <div className="flex justify-between items-center py-0">
                        <span className="font-normal">Gst ({order?.gstPercent}%)

                        </span>
                        <span className="font-normal">Rs {order?.TotalGST || order?.GSTAmount || order?.totalGST || order?.gstAmount}</span>
                    </div>
                )}

                {/* Row: Net Bill (always shown) */}
                <div className="flex justify-between items-center py-0">
                    <span className="font-medium">Net Bill:</span>
                    <span className="font-medium">Rs {order?.netBill}</span>
                </div>

                {/* Payment Details Section */}
                {paymentDetails && paymentDetails.length > 0 && (
                    <div className="border-t-2 border-black my-2 pt-2">
                        {paymentDetails.map((payment, index) => (

                            <>
                                <div key={index} className="flex justify-between items-center py-0">
                                    <span className="font-normal">
                                        {payment.PaymentModeName || payment.PaymentMode || "N/A"}
                                    </span>
                                    <span className="font-normal">Rs {payment.ReceivedAmount || payment.Amount || "0.00"}</span>
                                </div>

                                {payment.CashPaymentExists && payment.ReturnAmount > 0 && (
                                    <div key={`change-${index}`} className="flex justify-between items-center py-0">
                                        <span className="font-normal">Change Return </span>
                                        <span className="font-normal">Rs {payment.ReturnAmount} </span>
                                    </div>
                                )}
                            </>
                        ))}

                        {/* Payment Info Fields - Display once after all payment modes */}
                        {paymentDetailsInfo?.CareOff && (
                            <div className="flex justify-between items-center py-0 mt-1">
                                <span className="font-normal">Care Off </span>
                                <span className="font-normal">{paymentDetailsInfo.CareOff} </span>
                            </div>
                        )}

                        {paymentDetailsInfo?.BankName && (
                            <div className="flex justify-between items-center py-0">
                                <span className="font-normal">Bank </span>
                                <span className="font-normal">{paymentDetailsInfo.BankName} </span>
                            </div>
                        )}

                        {paymentDetailsInfo?.CardNumber && (
                            <div className="flex justify-between items-center py-0">
                                <span className="font-normal">Card Number </span>
                                <span className="font-normal">{paymentDetailsInfo.CardNumber} </span>
                            </div>
                        )}

                        {order?.PaymentDate && (
                            <div className="flex justify-between items-center py-0 mt-1">
                                <span className="font-normal">Payment Date/Time:</span>
                                <span className="font-normal">{moment(order.PaymentDate).format("YYYY-MM-DD HH:mm:ss")}</span>
                            </div>
                        )}
                    </div>
                )}
            </div>

            <br />

            <div className="flex flex-col w-full items-center text-black">
                <span className="italic">Powered by EatX</span>
                <span> Printed on: {getPrintTime()}</span>
            </div>
        </div>
    );
});

const OrderCheckoutBill = React.forwardRef(({ orderDetails, orderItems, paymentDetails, paymentDetailsInfo, paymentDetailsPartyAccountInfo, heading = "ORDER CHECKOUT BILL", onAfterPrint }, ref) => {

    const HideItemComments_CheckoutBill = useCheckPosSetting(HideItemComments);
    const HideDealItems_CheckoutBill = useCheckPosSetting(HideDealItems);

    const componentRef = useRef();

    const handlePrint = useReactToPrint({
        content: () => componentRef.current,
        documentTitle: `CheckoutBill_${orderDetails?.OrderNumber || orderDetails?.orderNumber || "unknown"}`,
        pageStyle: "@page { size: auto; margin: 0mm; }",
        onAfterPrint: onAfterPrint || (() => { })
    });

    // Expose print function via ref
    useImperativeHandle(ref, () => ({
        print: () => {
            if (orderDetails && orderItems && orderItems.length > 0) {
                // Use requestAnimationFrame to maintain user action context
                requestAnimationFrame(() => {
                    setTimeout(() => {
                        try {
                            handlePrint();
                        } catch (error) {
                            console.error("Error in OrderCheckoutBill print:", error);
                        }
                    }, 100);
                });
            }
        }
    }));

    // Don't auto-print - only print via ref to control sequence

    return (
        <div>
            {/* Hidden checkout bill template - only visible when printing */}
            <CheckoutBillTemplate
                ref={componentRef}
                order={orderDetails}
                items={orderItems}
                paymentDetails={paymentDetails}
                paymentDetailsInfo={paymentDetailsInfo}
                paymentDetailsPartyAccountInfo={paymentDetailsPartyAccountInfo}
                heading={heading}
                hide_ItemComments={HideItemComments_CheckoutBill}
                hide_DealItems={HideDealItems_CheckoutBill}
            />
        </div>
    );
});

export default OrderCheckoutBill;

